"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getEnvironment = exports.openWindow = exports.redirectToURL = exports.getLineOfBusiness = exports.getResourceId = exports.getHotelId = exports.debounce = void 0;
var DeviceUtils_1 = require("./DeviceUtils");
var DomUtils_1 = require("./DomUtils");
var GlobalConfig_1 = require("./GlobalConfig");
var LocaleUtils_1 = require("./LocaleUtils");
var URLUtils_1 = require("./URLUtils");
/**
 * Pattern used to obtain data from the application URL.
 *
 * 0: matching url
 * 1: 's' if using https protocol
 * 2: epc subdomain
 * 3: lab domain, if running in lab
 * 4: lab environment, if running in lab
 * 5: port, if running locally
 * 6: line of business
 * 7: application name
 * 8: remainder of the url, including page name, query string, etc.
 */
var URL_PATTERN_REGEX = /^http(s)?:\/\/(\w*).expediapartnercentral.com(\.(integration|lisqa7|lisqa8|stress)\.sb\.karmalab\.net)?(:\d+)?\/(\w*)\/(\w*)\/(.*)$/i;
function debounce(func, waitMilliseconds, immediate) {
    if (waitMilliseconds === void 0) { waitMilliseconds = 50; }
    if (immediate === void 0) { immediate = false; }
    var timeoutId;
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    return function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        var context = this;
        var doLater = function () {
            timeoutId = undefined;
            if (!immediate) {
                func.apply(context, args);
            }
        };
        var shouldCallNow = immediate && timeoutId === undefined;
        if (timeoutId !== undefined) {
            clearTimeout(timeoutId);
        }
        timeoutId = setTimeout(doLater, waitMilliseconds);
        if (shouldCallNow) {
            func.apply(context, args);
        }
    };
}
exports.debounce = debounce;
/**
 * Get the current hotel ID.
 */
exports.getHotelId = function () {
    var htid = URLUtils_1.getURLParameter('htid') || URLUtils_1.getURLParameter('propertyId');
    var context = GlobalConfig_1.getGlobalConfigContext();
    if (htid !== null) {
        return htid;
    }
    else if (context.hotelId !== undefined) {
        return context.hotelId;
    }
    return null;
};
/**
 * Line of business agnostic function to return the current resource identifier;
 * for instance, the hotel id, in the case of lodging applications.
 */
exports.getResourceId = function () {
    if (exports.getLineOfBusiness() === "lodging" /* LODGING */) {
        return exports.getHotelId();
    }
    return null;
};
/**
 * Get the line of business of the current application. If no URL is specified, the current window location is used.
 *
 * @param url
 */
exports.getLineOfBusiness = function (url) {
    var currentUrl = (function (url) {
        return url && url || typeof window !== 'undefined' && window.location.href || '';
    })(url);
    var urlData = currentUrl && URL_PATTERN_REGEX.exec(currentUrl);
    if (urlData) {
        // FIXME: EVEN IF WE HAVE A MATCH, DO WE WANT TO RETURN "INVALID" LOBS?
        return urlData[6];
    }
    return null;
};
/**
 * Redirect the from the current web page to the URL specified.
 */
exports.redirectToURL = function (url) {
    if (typeof window !== 'undefined' && url) {
        window.location.href = url;
    }
};
/**
 * Open a new browser window.
 */
exports.openWindow = function (url, windowName, windowFeatures) {
    if (windowName === void 0) { windowName = '_blank'; }
    if (windowFeatures === void 0) { windowFeatures = ''; }
    if (typeof window !== 'undefined' && (url || (url === '' && windowName !== '' && windowName !== '_blank'))) {
        return window.open(url, windowName, windowFeatures);
    }
    return null;
};
/**
 * Get the current application environment.  If no URL is specified, the current window location is used.
 */
exports.getEnvironment = function (url) {
    var currentUrl = url ? url.trim().toLowerCase() : typeof window !== 'undefined' && window.location.href || '';
    if (currentUrl.indexOf('.integration.') !== -1) {
        return "INT" /* INT */;
    }
    else if (currentUrl.indexOf('.lisqa7.') !== -1 || currentUrl.indexOf('.lisqa8.') !== -1 ||
        currentUrl.indexOf('.stress.') !== -1 || currentUrl.indexOf('expediapartnercentral') === -1) {
        return "TEST" /* TEST */;
    }
    return "PROD" /* PROD */;
};
exports.default = {
    /**
     * Get the value of the specified parameter name from the URL query string. Returns null if the parameter is not found.
     */
    getURLParameter: URLUtils_1.getURLParameter,
    /**
     * Get a key/value pair map of parameters from the URL query string.
     */
    getURLParameters: URLUtils_1.getURLParameters,
    /**
     * Get a copy of the global config object.
     */
    getGlobalConfig: GlobalConfig_1.getGlobalConfig,
    /**
     * Get a copy of the context object within the global config.
     */
    getGlobalConfigContext: GlobalConfig_1.getGlobalConfigContext,
    /**
     * Return the current locale value in the format [language code]-[country code]; i.e. en-us.
     *
     * The locale is resolved in the following order; first valid value found is used:
     *
     * - The URL `debugLocale` parameter
     * - The global config object
     * - The global i18n object
     *
     * If unable to determine the current locale, the default locale of 'en-us' is returned.
     *
     * @returns Current locale
     */
    getLocale: LocaleUtils_1.getLocale,
    /**
     * Get the current language direction associated with the current locale as specified in
     * the `dir` attribute of the body tag.  If the `dir` attribute is not available, the default
     * direction, "ltr", is returned.
     */
    getLanguageDirection: LocaleUtils_1.getLanguageDirection,
    /**
     * Get the current hotel ID.
     */
    getHotelId: exports.getHotelId,
    /**
     * Line of business agnostic function to return the current resource identifier;
     * for instance, the hotel id, in the case of lodging applications.
     */
    getResourceId: exports.getResourceId,
    /**
     * Redirect the from the current web page to the URL specified.
     */
    redirectToURL: exports.redirectToURL,
    /**
     * Open a new browser window.
     */
    openWindow: exports.openWindow,
    /**
     * Determine if the application is currently being rendered in a non-mobile browser.
     */
    isDesktop: DeviceUtils_1.isDesktop,
    /**
     * Determines if the application is running in either a mobile browser, or the mobile app.
     */
    isMobile: DeviceUtils_1.isMobile,
    /**
     * Determines if the application is currently being on a mobile device, but not within within the mobile app.
     */
    isMobileWeb: DeviceUtils_1.isMobileWeb,
    /**
     * Determines if the application is currently being rendered within the mobile app.
     */
    isMobileApp: DeviceUtils_1.isMobileApp,
    /**
     * Gets the value of a given cookie name. If the cookie is not found, an empty string is returned.
     */
    getCookie: function (cname) {
        var name = cname + "=";
        var ca = window.document.cookie.split(';');
        for (var i = 0, len = ca.length; i < len; i++) {
            var c = ca[i];
            while (c.charAt(0) === ' ') {
                c = c.substring(1);
            }
            if (c.indexOf(name) !== -1) {
                return c.substring(name.length, c.length);
            }
        }
        return '';
    },
    /**
     * Get the current application environment.  If no URL is specified, the current window location is used.
     */
    getEnvironment: exports.getEnvironment,
    /**
     * Get the line of business of the current application. If no URL is specified, the current window location is used.
     */
    getLineOfBusiness: exports.getLineOfBusiness,
    /**
     *
     */
    debounce: debounce,
    /**
     * Bind an event to the DOM, specifying the function that will be executed when the event is triggered.
     *
     * @param eventName The name of the event to attach to a target.
     * @param handler The callback function that will be executed when the event is triggered.
     * @param el Optional; the DOM element upon which to bind this event. If not specified, `window.document` is used.
     */
    attachEvent: DomUtils_1.attachEvent,
    /**
     * Trigger a given event, which will execute all event handlers that have been bound within its DOM tree.
     *
     * @param eventName The name of the event to trigger.
     */
    triggerEvent: DomUtils_1.triggerEvent,
    /**
     * Creates a URL-friendly string to use as a "slug".
     */
    slugify: URLUtils_1.slugify,
    /**
     * Gets the browser viewport dimensions
     */
    getViewport: DeviceUtils_1.getViewport,
    /**
     * Determines safe min/max window offset value of an absolutely positioned element
     * to prevent the absolutely positioned element to break outside of the left/right
     * edges of the viewport
     *
     * @param el DOM element to be absolutely positioned
     * @param offset Offset value (right or left) to be safe-checked
     * @return Safe offset value (px)
     */
    getSafeWindowOffset: DeviceUtils_1.getSafeWindowOffset
};
